import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowUpIcon
} from '@heroicons/react/24/outline';

interface FooterProps {
  onTermsClick: () => void;
  onCookiePolicyClick: () => void;
  onPrivacyClick: () => void;
}

const Footer: React.FC<FooterProps> = ({ onTermsClick, onCookiePolicyClick, onPrivacyClick }) => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    {
      name: 'WhatsApp',
      url: 'https://wa.me/917209213003',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      )
    }
  ];

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Process', href: '#process' },
    { name: 'Projects', href: '#projects' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Contact', href: '#contact' }
  ];

  return (
    <footer className="bg-gray-900 text-white relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-purple-600"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Main Footer Content */}
        <div className="py-12 sm:py-16">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-8">
            {/* Brand Section */}
            <div className="col-span-2 lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-3 sm:mb-4">
                  <img
                    src="/Assets/logo/PragyaTek Solutions.png"
                    alt="PragyaTek Solutions Logo"
                    className="h-8 sm:h-10 w-8 sm:w-10 object-cover rounded-full"
                  />
                </div>
                <p className="text-gray-400 mb-4 sm:mb-6 max-w-md text-sm sm:text-base leading-relaxed">
                  PragyaTek Solutions is a professional web & app development agency, a unit of Chakravega Technologies Private Limited (CIN: U62099JH2026PTC028403). Engineering Intelligence for a Safer and Smarter Future.
                </p>
                
                {/* Social Links */}
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={social.name}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2.5 sm:p-3 bg-gray-800 hover:bg-primary-600 rounded-lg transition-all duration-300 text-white"
                    >
                      <div className="w-5 h-5 sm:w-6 sm:h-6">
                        {social.icon}
                      </div>
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="col-span-1"
            >
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold mb-3 sm:mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-primary-400 transition-colors duration-200 text-xs sm:text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="col-span-1"
            >
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold mb-3 sm:mb-4">Get In Touch</h3>
              <div className="space-y-2.5 sm:space-y-3 text-gray-400 text-xs sm:text-sm">
                <p>
                  <a href="mailto:chakravegatechnologies@gmail.com" className="hover:text-primary-400 transition-colors break-words">
                    chakravegatechnologies@gmail.com
                  </a>
                </p>
                <p>
                  <a href="tel:+917209213003" className="hover:text-primary-400 transition-colors">
                    +91 72092 13003
                  </a>
                </p>
                <p>
                  <span>BIT Sindri, Dhanbad, Jharkhand - 828123</span>
                </p>
                <p>
                  <span>Mon-Sat: 9AM-9PM</span>
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-gray-400 text-xs sm:text-sm text-center sm:text-left"
            >
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                <span>© {currentYear} PragyaTek Solutions. A Unit of Chakravega Technologies Pvt. Ltd. All rights reserved.</span>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <button
                    onClick={onPrivacyClick}
                    className="text-gray-400 hover:text-primary-400 transition-colors underline-offset-4 hover:underline text-xs sm:text-sm"
                  >
                    Privacy
                  </button>
                  <span className="text-gray-600">•</span>
                  <button
                    onClick={onTermsClick}
                    className="text-gray-400 hover:text-primary-400 transition-colors underline-offset-4 hover:underline text-xs sm:text-sm"
                  >
                    Terms
                  </button>
                  <span className="text-gray-600">•</span>
                  <button
                    onClick={onCookiePolicyClick}
                    className="text-gray-400 hover:text-primary-400 transition-colors underline-offset-4 hover:underline text-xs sm:text-sm"
                  >
                    Cookies
                  </button>
                </div>
              </div>
            </motion.div>

            <motion.button
              onClick={scrollToTop}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.9 }}
              className="p-3 bg-primary-600 hover:bg-primary-700 rounded-lg transition-all duration-300"
            >
              <ArrowUpIcon className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 